import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2, Globe, MessageCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import confetti from 'canvas-confetti';

// ---------------------------------------------------------------------------
// SpillTeaHomePage — editorial dictionary-style company directory
// ---------------------------------------------------------------------------

const ALL_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const SpillTeaHomePage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [companies, setCompanies] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [hasReviewed, setHasReviewed] = useState(null);
  const [activeLetter, setActiveLetter] = useState(null);
  const confettiFired = useRef(false);

  // "Write a Review" dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [resumeCompanies, setResumeCompanies] = useState([]);
  const [reviewedCompanies, setReviewedCompanies] = useState([]);
  const dropdownRef = useRef(null);

  // Refs for letter sections
  const sectionRefs = useRef({});

  // Auth redirect
  useEffect(() => {
    if (!loading && !user) navigate('/connect-email');
  }, [user, loading, navigate]);

  // Gate check
  useEffect(() => {
    const checkAccess = async () => {
      if (!user) return;
      try {
        const { count, error } = await supabase
          .from('spill_tea_reviews')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);

        if (!error && count > 0) {
          setHasReviewed(true);
        } else {
          setHasReviewed(false);
          navigate('/spill-tea', { replace: true });
        }
      } catch {
        navigate('/spill-tea', { replace: true });
      }
    };
    if (user) checkAccess();
  }, [user, navigate]);

  // Load all companies aggregated from reviews
  useEffect(() => {
    const loadCompanies = async () => {
      if (!user || hasReviewed !== true) return;
      try {
        const { data, error } = await supabase
          .from('spill_tea_reviews')
          .select('company_name, company_domain, boba_rating');

        if (error) throw error;

        const map = {};
        (data || []).forEach((r) => {
          const domain = r.company_domain;
          if (!map[domain]) {
            map[domain] = { name: r.company_name, domain, ratings: [], reviewCount: 0 };
          }
          map[domain].ratings.push(r.boba_rating);
          map[domain].reviewCount += 1;
        });

        Object.values(map).forEach((c) => {
          c.avgRating = Math.round(c.ratings.reduce((a, b) => a + b, 0) / c.ratings.length);
        });

        const grouped = {};
        Object.values(map).forEach((c) => {
          const letter = c.name.charAt(0).toUpperCase();
          if (!grouped[letter]) grouped[letter] = [];
          grouped[letter].push(c);
        });

        Object.keys(grouped).forEach((letter) => {
          grouped[letter].sort((a, b) => a.name.localeCompare(b.name));
        });

        setCompanies(grouped);

        // Set initial active letter
        const sorted = Object.keys(grouped).sort();
        if (sorted.length > 0) setActiveLetter(sorted[0]);
      } catch (err) {
      } finally {
        setIsLoading(false);
      }
    };
    loadCompanies();
  }, [user, hasReviewed]);

  // Load resume companies + reviewed companies for the dropdown
  useEffect(() => {
    const loadDropdownData = async () => {
      if (!user || hasReviewed !== true) return;

      // --- Fetch user's reviewed company names ---
      try {
        const { data } = await supabase
          .from('spill_tea_reviews')
          .select('company_name')
          .eq('user_id', user.id);
        setReviewedCompanies((data || []).map((r) => r.company_name.toLowerCase().trim()));
      } catch { /* ignore */ }

      // --- Fetch resume companies (same multi-source fallback as SpillTeaPage) ---
      let found = [];

      try {
        const { data, error } = await supabase
          .from('user_resumes')
          .select('extracted_data')
          .eq('user_id', user.id)
          .order('uploaded_at', { ascending: false })
          .limit(1)
          .single();

        if (!error && data?.extracted_data) {
          const ed = data.extracted_data;
          if (Array.isArray(ed.companies) && ed.companies.length > 0) {
            found = ed.companies;
          }
          if (found.length === 0 && Array.isArray(ed.work_experience)) {
            const names = ed.work_experience.map((w) => w.company || w.employer || w.organization).filter(Boolean);
            if (names.length > 0) found = names;
          }
        }
      } catch { /* ignore */ }

      if (found.length === 0) {
        try {
          const { data, error } = await supabase
            .from('career_roadmaps')
            .select('form_data')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (!error && data?.form_data?.resume_data) {
            const rd = data.form_data.resume_data;
            if (Array.isArray(rd.companies) && rd.companies.length > 0) {
              found = rd.companies;
            }
            if (found.length === 0 && Array.isArray(rd.work_experience)) {
              const names = rd.work_experience.map((w) => w.company || w.employer || w.organization).filter(Boolean);
              if (names.length > 0) found = names;
            }
          }
        } catch { /* ignore */ }
      }

      const unique = [...new Set(found.map((c) => c.trim()).filter(Boolean))];
      setResumeCompanies(unique);
    };

    loadDropdownData();
  }, [user, hasReviewed]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Confetti on first load
  useEffect(() => {
    if (confettiFired.current || hasReviewed !== true) return;
    const timer = setTimeout(() => {
      confettiFired.current = true;
      const pastelColors = ['#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF', '#E0BBE4'];
      confetti({ particleCount: 40, spread: 70, startVelocity: 22, gravity: 0.7, scalar: 0.8, origin: { x: 0.5, y: 0.3 }, colors: pastelColors, ticks: 100 });
    }, 400);
    return () => clearTimeout(timer);
  }, [hasReviewed]);

  // No auto-scroll or intersection observer — user clicks a letter to scroll, then freely scrolls after


  const sortedLetters = Object.keys(companies).sort();
  const unreviewedCompanies = resumeCompanies.filter(
    (c) => !reviewedCompanies.includes(c.toLowerCase().trim())
  );

  if (loading || hasReviewed === null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ position: 'relative', fontFamily: "'Montserrat', sans-serif" }}>
      {/* Fixed sky background */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'url(/bluewhite.png)', backgroundSize: 'cover', backgroundPosition: 'center', zIndex: -1, transform: 'translateZ(0)', willChange: 'transform' }} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;900&family=Inter:wght@400;500;600;700&display=swap');

        .company-glass-card {
          background: linear-gradient(145deg, rgba(200, 210, 220, 0.45) 0%, rgba(180, 195, 210, 0.35) 50%, rgba(170, 185, 200, 0.3) 100%);
          border: 1px solid rgba(255, 255, 255, 0.45);
          backdrop-filter: blur(16px) saturate(160%);
          -webkit-backdrop-filter: blur(16px) saturate(160%);
          box-shadow: 0 8px 32px -8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.35);
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .company-glass-card:hover {
          background: rgba(255, 255, 255, 0.88);
          border-color: rgba(255, 255, 255, 0.7);
          box-shadow: 0 12px 40px -8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5);
          transform: translateY(-3px);
        }
        .company-glass-card:hover .card-company-name {
          color: #1e3a5f !important;
        }
        .company-glass-card:hover .card-icon {
          color: #475569 !important;
        }
        .company-glass-card:hover .card-text {
          color: #64748b !important;
        }

        .dropdown-glass {
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 0 20px 60px -15px rgba(0, 0, 0, 0.2), 0 4px 20px rgba(0, 0, 0, 0.08);
        }
      `}</style>

      {/* Header */}
      <header
        className="sticky top-0 z-40 border-b backdrop-blur-xl"
        style={{
          background: 'rgba(255, 252, 248, 0.65)',
          borderColor: 'rgba(255, 255, 255, 0.3)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.08)',
          WebkitBackdropFilter: 'blur(20px)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Left — back + title */}
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/roadmap')} className="rounded-xl shrink-0">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <span style={{ fontSize: '1.3rem' }}>🧋</span>
                <h1 className="text-xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif", color: '#2d1b0e' }}>
                  Spill the Tea
                </h1>
              </div>
            </div>

            {/* Right — Write a Review dropdown */}
            <div className="relative" ref={dropdownRef}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  borderColor: 'rgba(37,99,235,0.35)',
                  color: '#1e40af',
                  background: 'rgba(255,255,255,0.8)',
                }}
                className="gap-1.5"
              >
                + Write a Review
                <ChevronDown
                  className="w-3.5 h-3.5 transition-transform duration-200"
                  style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </Button>

              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    className="dropdown-glass absolute right-0 top-[calc(100%+8px)] w-64 rounded-2xl overflow-hidden z-50"
                  >
                    <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Companies on your resume
                      </p>
                    </div>
                    <div className="max-h-64 overflow-y-auto py-1">
                      {unreviewedCompanies.length === 0 ? (
                        <div className="px-4 py-6 text-center">
                          <p className="text-sm text-slate-400" style={{ fontFamily: "'Inter', sans-serif" }}>
                            You've reviewed all your companies! 🎉
                          </p>
                        </div>
                      ) : (
                        unreviewedCompanies.map((company, idx) => (
                          <button
                            key={idx}
                            className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-blue-50/80 transition-colors group"
                            onClick={() => {
                              setDropdownOpen(false);
                              navigate(`/spill-tea/${encodeURIComponent(company)}`);
                            }}
                          >
                            <span
                              className="text-sm font-medium text-slate-700 truncate"
                              style={{ fontFamily: "'Inter', sans-serif" }}
                            >
                              {company}
                            </span>
                            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 shrink-0 ml-2 transition-colors" />
                          </button>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Alphabet bar — all A-Z in one row, no scroll */}
      <div
        className="sticky top-[65px] z-30 backdrop-blur-md border-b"
        style={{ background: 'rgba(255,252,248,0.5)', borderColor: 'rgba(255,255,255,0.25)' }}
      >
        <div className="max-w-5xl mx-auto px-4 py-2.5">
          <div className="flex items-center justify-between">
            {ALL_LETTERS.map((letter) => {
              const hasEntries = sortedLetters.includes(letter);
              const isActive = activeLetter === letter;
              return (
                <button
                  key={letter}
                  data-alpha={letter}
                  onClick={() => {
                    if (hasEntries) {
                      setActiveLetter(letter);
                      const el = sectionRefs.current[letter];
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="flex items-center justify-center rounded-lg transition-all duration-150"
                  style={{
                    width: 32,
                    height: 32,
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: isActive ? 800 : 600,
                    fontSize: '0.8rem',
                    color: isActive
                      ? '#fff'
                      : hasEntries
                      ? '#1e3a5f'
                      : 'rgba(0,0,0,0.2)',
                    background: isActive
                      ? 'linear-gradient(135deg, #1e3a5f, #2563eb)'
                      : 'transparent',
                    cursor: hasEntries ? 'pointer' : 'default',
                    borderRadius: isActive ? '8px' : '6px',
                    boxShadow: isActive ? '0 3px 10px rgba(37,99,235,0.3)' : 'none',
                  }}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 pt-6 pb-20">
        {isLoading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : sortedLetters.length === 0 ? (
          <div className="text-center py-24">
            <span style={{ fontSize: '3.5rem' }}>🧋</span>
            <h3 className="text-2xl font-black mt-6 mb-3" style={{ fontFamily: "'Montserrat', sans-serif", color: '#1e3a5f' }}>
              The tea is brewing…
            </h3>
            <p className="text-slate-500 max-w-md mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
              No reviews yet. Be the first to spill the tea and help the community!
            </p>
            <Button
              className="mt-8"
              onClick={() => navigate('/spill-tea')}
              style={{
                background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
                color: '#fff',
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                borderRadius: 14,
                padding: '12px 28px',
              }}
            >
              Write a Review
            </Button>
          </div>
        ) : (
          <div className="space-y-12">
            {sortedLetters.map((letter) => (
              <section
                key={letter}
                ref={(el) => (sectionRefs.current[letter] = el)}
                data-letter={letter}
                className="scroll-mt-[120px]"
              >
                {/* Letter heading */}
                <div className="flex items-center gap-4 mb-5">
                  <span
                    className="text-5xl md:text-6xl font-black leading-none"
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      color: '#1e3a5f',
                      opacity: 0.75,
                    }}
                  >
                    {letter}
                  </span>
                  <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(30,58,95,0.2), transparent)' }} />
                </div>

                {/* Company grid — 4 columns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {companies[letter].map((company, idx) => (
                    <CompanyCard key={company.domain} company={company} idx={idx} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CompanyCard — glass card in the dictionary grid
// ---------------------------------------------------------------------------

const CompanyCard = ({ company, idx }) => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.04, duration: 0.3 }}
    >
      <div
        className="company-glass-card rounded-2xl p-5 cursor-pointer"
        onClick={() => navigate(`/spill-tea/reviews/${encodeURIComponent(company.domain)}`)}
      >
        {/* Company name */}
        <h4
          className="card-company-name text-[17px] font-bold truncate mb-3"
          style={{ fontFamily: "'Montserrat', sans-serif", color: 'rgba(255,255,255,0.95)' }}
        >
          {company.name}
        </h4>

        {/* Boba rating dots */}
        <div className="flex gap-1 mb-4">
          {Array.from({ length: 10 }, (_, i) => (
            <div
              key={i}
              className="rounded-full"
              style={{
                width: 10,
                height: 10,
                background: i < company.avgRating
                  ? 'linear-gradient(145deg, #5C3A1E, #8B6914)'
                  : 'rgba(255,255,255,0.2)',
                border: i < company.avgRating ? '1px solid rgba(139,105,20,0.4)' : '1px solid rgba(255,255,255,0.15)',
              }}
            />
          ))}
        </div>

        {/* Icons row */}
        <div className="flex items-center gap-4">
          <a
            href={`https://${company.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="card-icon transition-colors p-1 -m-1 rounded-lg hover:bg-white/20"
            style={{ color: 'rgba(255,255,255,0.6)' }}
          >
            <Globe className="w-5 h-5" />
          </a>
          <div
            className="flex items-center gap-1.5 card-text p-1 -m-1 rounded-lg"
            style={{ color: 'rgba(255,255,255,0.6)' }}
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-sm font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
              {company.reviewCount}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SpillTeaHomePage;
